import {h} from 'hastscript'

import type {JSONObject} from '../../language-tools/json/types'
import {HastElementWidget} from './hast-element'

export const keyAttr = (widgetName: string) => `data-${widgetName}-key`

export const propsAttr = (widgetName: string) => `data-${widgetName}-props`

/**
 * CodeMirror widget decoration implementation for injecting React components into the editor. Outputs an empty
 * container to select and render via the `useReactWidgets` hook.
 *
 * Instructions for implementing a new React component widget:
 *
 * 1. Create a new React component that accepts serializable props (props that conform to `JSONObject`).
 * 2. Implement a new widget class that extends this class, using a unique **lowercase** `widgetName` for the class.
 * 3. Render the widget as normal using CodeMirror `Decoration.widget`.
 * 4. Hydrate the widget by calling `useReactWidgets` with the editor's React component and rendering the result.
 */
export abstract class ReactComponentWidget<Props extends JSONObject> extends HastElementWidget {
  readonly key: string
  readonly props: Props
  readonly #widgetName: string

  /**
   *
   * @param widgetName The name of this widget class, kebab-case. Must be consistent for all instances of this widget,
   * but unique across all `ReactComponentWidget` implementations.
   * @param key Unique key for this widget. Must be unique across all instances of this widget, and constant for the
   * lifetime of this instance.
   * @param props Props object to pass through to the React component. Must be JSON-serializable.
   */
  constructor(widgetName: string, key: string, props: Props) {
    // Render an empty container to be selected and rendered via React
    super(
      h('div', {
        [propsAttr(widgetName)]: JSON.stringify(props),
        [keyAttr(widgetName)]: key,
      }),
    )
    this.key = key
    this.#widgetName = widgetName
    this.props = props
  }

  /** Enables updating the DOM attributes rather than re-creating the element (and thus re-mounting the widget). */
  override updateDOM(dom: HTMLElement): boolean {
    // Ensure the key remains the same across updates
    const key = dom.getAttribute(keyAttr(this.#widgetName))
    if (key !== this.key) return false

    dom.setAttribute(propsAttr(this.#widgetName), JSON.stringify(this.props))
    return true
  }

  /**
   * Should return `false` any time anything changes; CodeMirror will then call `updateDOM` and only re-mount if that
   * also returns `false`.
   */
  override eq(other: ReactComponentWidget<Props>) {
    return (
      this.key === other.key &&
      (this.props === other.props || JSON.stringify(this.props) === JSON.stringify(other.props))
    )
  }
}
